import { Link as RouteLink, Outlet } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Button, ButtonGroup, Link } from '@chakra-ui/react'
import { graphql } from '@/__generated__/gql'
import { useQueryParams } from '@/hooks/use-query-params'
import { usePagination } from '@/hooks/use-pagination'
import { FullLayout } from '@/components/full-layout'
import { ContainerBox } from '@/components/container-box'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/components/table'
import { Row, Rows } from '@/components/rows'
import { Heading } from '@/components/heading'
import { DownloadExportButton } from '@/components/download-export-button'
import { SearchButtonField } from '@/components/search-button-field'
import { CategorySelect } from '@/components/category-select'
import { SortDirectionLink } from '@/components/sort-direction-link'
import { LoadingSection } from '@/components/loading-section'

export const suppliersDocument = graphql(`
  query Suppliers($input: SuppliersConnectionInputArgs!) {
    suppliers(input: $input) {
      totalCount
      results {
        id
        name
        publicRef
        rating
        address {
          postalArea
        }
      }
    }
  }
`)

export function SuppliersRoute() {
  const [queryParams, setQueryParams] = useQueryParams()
  const { Pagination, variables } = usePagination()
  const { data, loading, error } = useQuery(suppliersDocument, {
    variables: {
      input: {
        ...variables,
        ...queryParams,
        page: queryParams.page ? Number(queryParams.page) : undefined,
      },
    },
  })
  const suppliers = data?.suppliers.results || []
  const totalCount = data?.suppliers.totalCount || 0

  if (error) throw error

  return (
    <FullLayout>
      <ContainerBox>
        <Rows>
          <Row>
            <Heading
              title="Leverandører"
              actions={
                <>
                  <SearchButtonField
                    placeholder="Søk på navn eller organisasjonsnummer"
                    query={queryParams.query}
                    onChange={(query) => setQueryParams({ query })}
                  />
                  <CategorySelect
                    width={140}
                    size="sm"
                    prompt="Alle kategorier"
                    value={queryParams.categoryId}
                    onChange={(id) => setQueryParams({ categoryId: id || '' })}
                  />
                  <ButtonGroup>
                    <DownloadExportButton
                      label="Last ned (Excel)"
                      file="suppliers.xlsx"
                    />
                    <Button
                      as={RouteLink}
                      to="new"
                      size="sm"
                      colorScheme="blue"
                    >
                      Ny leverandør +
                    </Button>
                  </ButtonGroup>
                </>
              }
            />
          </Row>
          <Row>
            <Table>
              <Thead>
                <Tr>
                  <Th>
                    <SortDirectionLink field="supplier.publicRef" label="#" />
                  </Th>
                  <Th>
                    <SortDirectionLink
                      field="supplier.name"
                      label="Leverandør"
                    />
                  </Th>
                  <Th>
                    <SortDirectionLink field="supplier.rating" label="Rating" />
                  </Th>
                  <Th>Poststed</Th>
                </Tr>
              </Thead>
              <Tbody>
                {suppliers.map((supplier) => (
                  <Tr key={supplier.id}>
                    <Td>{supplier.publicRef}</Td>
                    <Td>
                      <Link as={RouteLink} to={supplier.id}>
                        {supplier.name}
                      </Link>
                    </Td>
                    <Td>{supplier.rating}</Td>
                    <Td>{supplier.address.postalArea}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {loading && <LoadingSection />}
          </Row>
          {totalCount > variables.limit && (
            <Row>
              <Pagination total={totalCount} />
            </Row>
          )}
        </Rows>
      </ContainerBox>

      <Outlet />
    </FullLayout>
  )
}
